import React, { useCallback, useEffect, useState } from 'react';
import size from 'lodash/size';
import { useHistory, useLocation } from 'react-router-dom';
import { Col, Container, Row } from '@yme/atoms/grid';
import { InfiniteScroll } from '@yme/atoms/infinite-scroll';
import { buildEmptyItemsArray, usePaginationConfig } from '@yme/atoms/pagination';
import { SpinnerLoader } from '@yme/atoms/spinner-loader';
import { DEFAULT_PER_PAGE_VALUES } from '@yme/common/constants';
import { useWindowSize } from '@yme/common/hooks';
import { IndentWrapper } from '@yme/components/Separator';
import { GridSkeleton } from '@yme/molecules/grid-skeleton';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { getAdaptivePerPageLimit } from '@yme/utils/common';
import { FilterPanel } from './FilterPanel';
import { GridContent } from './GridContent';
import { DEFAULT_PAGE_SEARCH_INFINITE_SCROLL, EVENTS_TYPE, INFINITE_SCROLL_OFFSET_DESKTOP, INFINITE_SCROLL_OFFSET_MOBILE, NEWS_EVENTS_GRID_TEST_ID, } from './constants';
import { buildDataFromURL, buildRequest, getCategoriesTitleList, getEmptyArrayData, getSelectedCategoryList, getURLWithFilters, getYearsList, } from './helpers';
import { HeaderStyled } from './styled';
export function NewsEventsGrid({ articlesData, isLoading, isLoaded, dataForGridSettings, isRightToLeft, language, commonLabels, isInEditor, requestCallback, }) {
    const { title, categories, yearFrom, isRemoveBottomIndent, labels, gridType } = dataForGridSettings;
    const location = useLocation();
    const width = window.innerWidth;
    const defaultPerPageLimit = getAdaptivePerPageLimit(width);
    const [categoriesTitleList, setCategoriesTitleList] = useState([]);
    const [yearsTitleList, setYearsTitleList] = useState([]);
    const [yearsData, setYearsData] = useState(null);
    const [categoriesData, setCategoriesData] = useState(null);
    const [mobileSelectedOptions, setMobileSelectedOptions] = useState(null);
    const [dataRequest, setDataRequest] = useState('');
    const [isMobileChanged, setIsMobileChanged] = useState(false);
    const [isSizeChanged, setIsSizeChanged] = useState(false);
    const [data, setData] = useState({
        count: 0,
        limit: defaultPerPageLimit,
        total: 1,
        categories: null,
        years: null,
        currentPage: 1,
        isDefault: true,
    });
    const { isMobileView, isTabletView, isInfiniteDesktopView } = useWindowSize();
    const articlesLength = articlesData?.results?.length;
    const articles = articlesData?.results;
    const isAllItemsLoaded = size(articlesData?.results) === articlesData?.total;
    const { newsEventsGrid_category_label: filterCategoriesTitle, newsGrid_yearFilter_label: filterYearsTitle, } = labels;
    const history = useHistory();
    const pagePath = location.pathname.replace('.html', '');
    const paginationConfigData = {
        currentPage: data.currentPage,
        perPageOptions: DEFAULT_PER_PAGE_VALUES,
        activeElement: data.limit,
        countItems: Math.floor(data.total / data.limit),
        items: buildEmptyItemsArray(getEmptyArrayData(data.total, data.limit)),
    };
    const { activeItemValue, setCurrentPerPageValue } = usePaginationConfig({
        paginationData: paginationConfigData,
        isMobileView,
    });
    useEffect(() => {
        if (!location.search) {
            history.push(DEFAULT_PAGE_SEARCH_INFINITE_SCROLL);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const params = buildDataFromURL(location.search, labels, categories);
        setData((prevData) => ({
            ...prevData,
            currentPage: params.page,
            categories: params.categories || null,
            years: params.years || null,
            isDefault: false,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);
    useEffect(() => {
        if (data.limit && !data.isDefault) {
            const request = buildRequest({
                categories: getSelectedCategoryList(categories, data?.categories?.value),
                years: data?.years?.value,
                offset: 0,
                limit: data.limit,
                pagePath,
                type: gridType,
            });
            setDataRequest(request);
            setIsSizeChanged(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.categories, data.years, data.total, data.isDefault, data.limit]);
    useEffect(() => {
        if (dataRequest) {
            requestCallback(dataRequest);
        }
    }, [dataRequest, requestCallback]);
    useEffect(() => {
        if (articlesData) {
            const { count, total } = articlesData;
            setData((prevData) => ({
                ...prevData,
                total,
                count,
            }));
        }
    }, [articlesData]);
    useEffect(() => {
        if (activeItemValue !== data.currentPage) {
            const urlCategories = getSelectedCategoryList(categories, data?.categories?.value);
            const urlYears = data?.years?.value;
            const url = getURLWithFilters({
                categories: urlCategories,
                years: urlYears,
            });
            history.push(url);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeItemValue]);
    useEffect(() => {
        if (!data.isDefault || isSizeChanged) {
            const urlCategories = getSelectedCategoryList(categories, data?.categories?.value);
            const urlYears = data?.years?.value;
            const url = getURLWithFilters({
                categories: urlCategories,
                years: urlYears,
            });
            history.push(url);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.limit]);
    useEffect(() => {
        if (categories?.length) {
            setCategoriesTitleList(getCategoriesTitleList(categories));
        }
    }, [categories]);
    useEffect(() => {
        if (yearFrom) {
            setYearsTitleList(getYearsList(yearFrom));
        }
    }, [yearFrom]);
    useEffect(() => {
        if (!data.isDefault) {
            const categoriesValues = categoriesData
                ?.filter(({ checked }) => checked)
                .map(({ value }) => value);
            const yearsValues = yearsData?.filter(({ checked }) => checked).map(({ value }) => value);
            const categoriesDataForUrl = getSelectedCategoryList(categories, categoriesValues);
            const url = getURLWithFilters({
                categories: categoriesDataForUrl,
                years: yearsValues,
            });
            history.push(url);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoriesData, yearsData]);
    useEffect(() => {
        const isMobileOptions = mobileSelectedOptions?.some((option) => Boolean(option.name));
        if (isMobileOptions) {
            setIsMobileChanged(true);
        }
        if (!data.isDefault && isMobileChanged) {
            const checkedCategories = mobileSelectedOptions?.find((option) => option.name === filterCategoriesTitle)?.value;
            const yearsMobile = mobileSelectedOptions?.find((option) => option.name === filterYearsTitle)?.value;
            const categoriesMobile = getSelectedCategoryList(categories, checkedCategories);
            const url = getURLWithFilters({
                categories: categoriesMobile,
                years: yearsMobile,
            });
            history.push(url);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mobileSelectedOptions, isMobileChanged]);
    useEffect(() => {
        const currentLimit = getAdaptivePerPageLimit(width);
        setData((prevData) => ({
            ...prevData,
            limit: currentLimit,
        }));
        if (!data.isDefault) {
            setIsSizeChanged(true);
            setCurrentPerPageValue(currentLimit);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobileView, isTabletView, isInfiniteDesktopView]);
    const fetchMoreItems = useCallback(() => {
        if (!isAllItemsLoaded) {
            const request = buildRequest({
                categories: getSelectedCategoryList(categories, data?.categories?.value),
                years: data?.years?.value,
                offset: articlesData ? articlesData?.offset + data.limit : 0,
                limit: data.limit,
                pagePath,
                type: gridType,
            });
            setDataRequest(request);
        }
    }, [
        articlesData,
        categories,
        data?.categories?.value,
        data.limit,
        data?.years?.value,
        gridType,
        isAllItemsLoaded,
        pagePath,
    ]);
    if (!title) {
        return null;
    }
    return (React.createElement(IndentWrapper, { isRemoveBottomIndent: isRemoveBottomIndent },
        React.createElement(Container, { isRightToLeft: isRightToLeft, dataTestId: NEWS_EVENTS_GRID_TEST_ID, id: NEWS_EVENTS_GRID_TEST_ID },
            React.createElement(Row, null,
                React.createElement(Col, null,
                    React.createElement(FlexWrapper, { flexDirection: gridType === EVENTS_TYPE && isInfiniteDesktopView ? 'row' : 'column', justifyContent: gridType === EVENTS_TYPE && isInfiniteDesktopView ? 'space-between' : 'flex-start' },
                        React.createElement(HeaderStyled, null, title),
                        React.createElement(FilterPanel, { filterData: data, labels: labels, setYearsData: setYearsData, setCategoriesData: setCategoriesData, setMobileSelectedOptions: setMobileSelectedOptions, yearsTitleList: yearsTitleList, categoriesTitleList: categoriesTitleList, isRightToLeft: isRightToLeft, articlesLength: articlesLength, commonLabels: commonLabels, gridType: gridType })))),
            !isLoaded && !isInEditor ? (React.createElement(Row, null,
                React.createElement(GridSkeleton, { currentPerPageValue: defaultPerPageLimit }))) : null,
            isLoaded && (React.createElement(InfiniteScroll, { onReachEnd: fetchMoreItems, offset: isMobileView ? INFINITE_SCROLL_OFFSET_MOBILE : INFINITE_SCROLL_OFFSET_DESKTOP },
                React.createElement(GridContent, { articlesData: articlesData, language: language, isRightToLeft: isRightToLeft, articles: articles, labels: labels, isLoaded: isLoaded, commonLabels: commonLabels, gridType: gridType })))),
        React.createElement(SpinnerLoader, { isLoading: isLoading, fullWidth: true })));
}
